// icsFeeds.js — Kalender-Abos (ICS-URLs) für den Parallelbetrieb.
//
// Die Praxis hinterlegt die geheime ICS-URL ihres bestehenden Kalenders
// (Outlook "Kalender veröffentlichen" / Google "Privatadresse im
// iCal-Format"). dida holt die Termine beim App-Start und auf Knopfdruck.
//
// LIVE-Modus: Abruf über die Edge Function "ics-proxy" (CORS).
// DEMO-Modus: direkter fetch-Versuch — scheitert bei den meisten Quellen
// an CORS, mit verständlicher Fehlermeldung.
import { supabase, isSupabaseConfigured } from "./supabase";
import { parseIcs, mergeEventsIntoApts } from "./ics";

const KEY = "dida_ics_feeds";

export const listFeeds = () => { try { const s = localStorage.getItem(KEY); if (s) return JSON.parse(s); } catch {} return []; };
const save = (feeds) => { try { localStorage.setItem(KEY, JSON.stringify(feeds)); } catch {} };

export function addFeed({ url, label, behandler, type }) {
  const feeds = listFeeds();
  feeds.push({ id: Date.now(), url: url.trim(), label: label.trim() || urlLabel(url), behandler, type, lastSync: null, lastResult: null });
  save(feeds);
  return feeds;
}

export function removeFeed(id) {
  const feeds = listFeeds().filter(f => f.id !== id);
  save(feeds);
  return feeds;
}

const urlLabel = (url) => { try { return new URL(url).hostname; } catch { return "Kalender"; } };

async function fetchIcs(url) {
  if (isSupabaseConfigured) {
    const { data, error } = await supabase.functions.invoke("ics-proxy", { body: { url } });
    if (error) throw new Error(error.message || "Edge Function nicht erreichbar — ist ics-proxy deployt?");
    if (data?.error) throw new Error(data.error);
    if (!data?.ics) throw new Error("Leere Antwort vom Kalender-Proxy.");
    return data.ics;
  }
  // Demo-Modus: direkter Versuch (funktioniert nur bei CORS-offenen Quellen)
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.text();
}

// Ein Abo synchronisieren; gibt {added, updated, events} zurück.
export async function syncFeed(feed) {
  const text = await fetchIcs(feed.url);
  const parsed = parseIcs(text);
  if (!parsed) throw new Error("Kein gültiger iCalendar-Inhalt.");
  const { added, updated } = await mergeEventsIntoApts(parsed.events, { behandler: feed.behandler, type: feed.type });
  const feeds = listFeeds().map(f => f.id === feed.id
    ? { ...f, lastSync: new Date().toLocaleString("de-DE"), lastResult: { added, updated, events: parsed.events.length, skipped: parsed.skipped } }
    : f);
  save(feeds);
  return { added, updated, events: parsed.events.length, skipped: parsed.skipped };
}

// Alle Abos synchronisieren (App-Start) — Fehler pro Feed schlucken,
// damit ein kaputtes Abo den Start nicht stört.
export async function syncAllFeeds() {
  const feeds = listFeeds();
  for (const f of feeds) {
    try { await syncFeed(f); }
    catch (e) {
      const updatedFeeds = listFeeds().map(x => x.id === f.id ? { ...x, lastResult: { error: e?.message || String(e) } } : x);
      save(updatedFeeds);
    }
  }
  return listFeeds();
}
